import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    DestroyRef,
    ElementRef,
    OnDestroy,
    Type,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, filter, fromEvent, fromEventPattern, takeUntil } from 'rxjs';
import { TooltipPosition } from 'shared/interfaces/tooltip.interface';

@Component({
    selector: 'app-tooltip',
    standalone: true,
    templateUrl: './tooltip.component.html',
    styleUrls: ['./tooltip.component.scss'], // Note: "styleUrl" -> "styleUrls"
})
export class TooltipComponent implements OnDestroy {
    @ViewChild('tooltipContentContainer', { read: ViewContainerRef }) tooltipContainer!: ViewContainerRef;

    protected componentRef!: ComponentRef<any>;
    private observer: Subject<any> = new Subject();

    private onShowCallback: () => void = () => {};
    private onHideCallback: () => void = () => {};

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private destroyRef: DestroyRef,
        private elementRef: ElementRef,
    ) {}

    /**
     * Creates and shows the tooltip for the given component, positions it relative to the anchor element,
     * passes in data, and wires up "close on scroll/outside-click/ESC" logic.
     *
     * @param component   - The component class to render as tooltip content
     * @param anchorElementRef - The anchor (activator) element
     * @param data        - Data to pass into the tooltip component instance
     * @param position    - Tooltip position relative to the anchor; defaults to 'right'
     */
    public showTooltip(
        component: Type<any>,
        anchorElementRef: ElementRef,
        data?: { [key: string]: any },
        position: TooltipPosition = 'right',
        showHtmlContent: boolean = false,
        tooltipTextWidthCharacters: number = 120,
        onShowCallback: () => void = () => {},
        onHideCallback: () => void = () => {},
    ) {
        this.onShowCallback = onShowCallback;
        this.onHideCallback = onHideCallback;

        this.hideTooltip();

        this.componentRef = this.tooltipContainer.createComponent(component);
        if (data) {
            this.componentRef.instance.data = data;
            this.componentRef.instance.showHtmlContent = showHtmlContent;
            this.componentRef.instance.tooltipTextWidthCharacters = tooltipTextWidthCharacters;
        }

        this.changeDetectorRef.detectChanges();
        this.positionTooltip(anchorElementRef.nativeElement, this.elementRef.nativeElement, position);

        fromEventPattern<Event>(
            (handler) => document.addEventListener('scroll', handler, true),
            (handler) => document.removeEventListener('scroll', handler, true),
        )
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.observer),
                filter((event: Event) => !this.elementRef.nativeElement.contains(event.target as Node)),
            )
            .subscribe(() => this.hideTooltip());

        fromEvent(document, 'click')
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.observer))
            .subscribe((event: Event) => {
                const mouseEvent = event as MouseEvent;
                if (!this.elementRef.nativeElement.contains(mouseEvent.target)) {
                    this.hideTooltip();
                }
            });

        fromEvent(document, 'keydown')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.observer),
                filter((event: Event) => (event as KeyboardEvent).key === 'Escape'),
            )
            .subscribe(() => this.hideTooltip());

        this.onShowCallback?.();
    }

    /**
     * Positions the tooltip based on the given `position` value,
     * it prioritizes *centered* relative to the anchor, but will move the tooltip if there is no room in the viewport.
     */
    private positionTooltip(anchorElement: HTMLElement, tooltipElement: HTMLElement, preferredPosition: TooltipPosition) {
        const anchorRect = anchorElement.getBoundingClientRect();
        const tooltipRect = tooltipElement.getBoundingClientRect();
        const offset = 8;

        const { top, left } = this.calculateIdealPosition(anchorRect, tooltipRect, preferredPosition, offset);

        const adjustedPosition = this.adjustForViewport({ top, left }, tooltipRect, anchorRect, preferredPosition, offset);

        tooltipElement.style.position = 'absolute';
        tooltipElement.style.top = `${adjustedPosition.top}px`;
        tooltipElement.style.left = `${adjustedPosition.left}px`;

        tooltipElement.setAttribute('tooltip-pos', adjustedPosition.finalPosition);
    }

    /**
     * Calculates initial top/left positioning based on the tooltip's preferred position.
     */
    private calculateIdealPosition(
        anchorRect: DOMRect,
        tooltipRect: DOMRect,
        position: TooltipPosition,
        offset: number,
    ): { top: number; left: number } {
        switch (position) {
            case 'top':
                return {
                    top: anchorRect.top - tooltipRect.height - offset,
                    left: anchorRect.left + (anchorRect.width - tooltipRect.width) / 2,
                };
            case 'bottom':
                return {
                    top: anchorRect.bottom + offset,
                    left: anchorRect.left + (anchorRect.width - tooltipRect.width) / 2,
                };
            case 'left':
                return {
                    top: anchorRect.top + (anchorRect.height - tooltipRect.height) / 2,
                    left: anchorRect.left - tooltipRect.width - offset,
                };
            case 'right':
                return {
                    top: anchorRect.top + (anchorRect.height - tooltipRect.height) / 2,
                    left: anchorRect.right + offset,
                };
            case 'topLeft':
                return {
                    top: anchorRect.top - tooltipRect.height - offset,
                    left: anchorRect.left,
                };
            case 'topRight':
                return {
                    top: anchorRect.top - tooltipRect.height - offset,
                    left: anchorRect.right - tooltipRect.width,
                };
            case 'bottomLeft':
                return {
                    top: anchorRect.bottom + offset,
                    left: anchorRect.left,
                };
            case 'bottomRight':
                return {
                    top: anchorRect.bottom + offset,
                    left: anchorRect.right - tooltipRect.width,
                };
            default:
                return {
                    top: anchorRect.top + (anchorRect.height - tooltipRect.height) / 2,
                    left: anchorRect.right + offset,
                };
        }
    }

    /**
     * Checks if a given horizontal position fits within the viewport.
     */
    private fitsHorizontally(posLeft: number, tooltipRect: DOMRect, offset: number): boolean {
        return posLeft >= offset && posLeft + tooltipRect.width <= window.innerWidth - offset;
    }

    /**
     * Checks if a given vertical position fits within the viewport.
     */
    private fitsVertically(posTop: number, tooltipRect: DOMRect, offset: number): boolean {
        return posTop >= offset && posTop + tooltipRect.height <= window.innerHeight - offset;
    }

    /**
     * Adjusts the tooltip position if it would overflow the viewport.
     */
    private adjustForViewport(
        position: { top: number; left: number },
        tooltipRect: DOMRect,
        anchorRect: DOMRect,
        initialPosition: TooltipPosition,
        offset: number,
    ): { top: number; left: number; finalPosition: string } {
        let { top, left } = position;
        let finalPosition: string = initialPosition;

        // Adjust horizontal positions
        if (initialPosition === 'left' && left < offset && this.fitsHorizontally(anchorRect.right + offset, tooltipRect, offset)) {
            left = anchorRect.right + offset;
            finalPosition = 'right';
        } else if (
            initialPosition === 'right' &&
            left + tooltipRect.width > window.innerWidth - offset &&
            this.fitsHorizontally(anchorRect.left - tooltipRect.width - offset, tooltipRect, offset)
        ) {
            left = anchorRect.left - tooltipRect.width - offset;
            finalPosition = 'left';
        }

        // Adjust vertical positions
        const isTopAligned = initialPosition === 'top' || initialPosition === 'topLeft' || initialPosition === 'topRight';
        const isBottomAligned = initialPosition === 'bottom' || initialPosition === 'bottomLeft' || initialPosition === 'bottomRight';

        if (isTopAligned && top < offset && this.fitsVertically(anchorRect.bottom + offset, tooltipRect, offset)) {
            top = anchorRect.bottom + offset;
            finalPosition = initialPosition.replace('top', 'bottom').toLowerCase();
        } else if (
            isBottomAligned &&
            top + tooltipRect.height > window.innerHeight - offset &&
            this.fitsVertically(anchorRect.top - tooltipRect.height - offset, tooltipRect, offset)
        ) {
            top = anchorRect.top - tooltipRect.height - offset;
            finalPosition = initialPosition.replace('bottom', 'top').toLowerCase();
        }

        // Adjust corner horizontal alignment
        if (
            initialPosition === 'bottomLeft' &&
            left + tooltipRect.width > window.innerWidth - offset &&
            this.fitsHorizontally(anchorRect.right - tooltipRect.width, tooltipRect, offset)
        ) {
            left = anchorRect.right - tooltipRect.width;
            finalPosition = finalPosition.replace('Left', 'Right').replace('left', 'right');
        } else if (initialPosition === 'bottomRight' && left < offset && this.fitsHorizontally(anchorRect.left, tooltipRect, offset)) {
            left = anchorRect.left;
            finalPosition = finalPosition.replace('Right', 'Left').replace('right', 'left');
        } else if (
            initialPosition === 'topLeft' &&
            left + tooltipRect.width > window.innerWidth - offset &&
            this.fitsHorizontally(anchorRect.right - tooltipRect.width, tooltipRect, offset)
        ) {
            left = anchorRect.right - tooltipRect.width;
            finalPosition = finalPosition.replace('Left', 'Right').replace('left', 'right');
        } else if (initialPosition === 'topRight' && left < offset && this.fitsHorizontally(anchorRect.left, tooltipRect, offset)) {
            left = anchorRect.left;
            finalPosition = finalPosition.replace('Right', 'Left').replace('right', 'left');
        }

        // Final boundary checks to keep tooltip within viewport
        top = Math.min(Math.max(offset, top), window.innerHeight - tooltipRect.height - offset);
        left = Math.min(Math.max(offset, left), window.innerWidth - tooltipRect.width - offset);

        return { top, left, finalPosition };
    }

    /**
     * Clears the tooltip from the container and notifies any open subscriptions to close.
     */
    public hideTooltip(): void {
        if (this.tooltipContainer) {
            this.tooltipContainer.clear();
        }
        this.observer.next(undefined);
        this.onHideCallback?.();
    }

    /**
     * Returns true if the tooltip container has any components rendered.
     */
    public isTooltipOpen(): boolean {
        return this.tooltipContainer && this.tooltipContainer.length > 0;
    }

    public ngOnDestroy(): void {
        this.hideTooltip();
    }
}
